import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import {useCallback, useEffect, useRef, useState} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {
  ORDER_FILTER,
  SEARCH_FILTERS,
  SEARCH_TYPE,
} from '@/src/app/constants/search';
import useUserActions from '@/src/app/store/user/useUserActions';
import {getAuthorizationToken} from '@/src/app/utils/authorizationToken';
import transforCoursesData from '@/src/app/utils/transforCoursesData';
import {transformClinicDataForCard} from '@/src/app/utils/transformClinicData';
import getTransformedPosts from '@/src/app/utils/transformedPosts';
import transformUserData from '@/src/app/utils/transformUserData';
import useDebounce from '@/src/hooks/useDebounce';
import useEmploymentTypes from '@/src/hooks/useEmploymentTypes';
import useLoading from '@/src/hooks/useLoading';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import getDataForVacancyItem from '@/src/jobPortal/allVacancies/styles/getDataForVacancyItem';
import fetchClinicsForUnitedTab from '@/src/Search/utils/fetchClinicsForUnitedTab';
import fetchCoursesForUnitedTab from '@/src/Search/utils/fetchCoursesForUnitedTab';
import fetchTopicsForUnitedTab from '@/src/Search/utils/fetchTopiscForUnitedTab';
import fetchUsersForUnitedTab from '@/src/Search/utils/fetchUsersForUnitedTab';
import fetchVacanciesForUnitedTab from '@/src/Search/utils/fetchVacanciesForUnitedTab';
import {useSearchFilters} from '@/src/Search/utils/useSearchFilters';

import MultipleSearchLayout from '../components/MultipleSearchLayout';

const MultipleSearch = () => {
  const {user} = useUserActions();
  const {employmentTypes} = useEmploymentTypes();
  const initialQuerySetRef = useRef(false);
  const [coursesData, setCoursesData] = useState(null);
  const [topicsData, setTopicsData] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [clinicsData, setClinicsData] = useState(null);
  const [vacanciesData, setVacanciesData] = useState(null);
  const [fetchedFilters, setFetchedFilters] = useState({query: null});
  const {query, locale, isReady: isRouterReady} = useRouter();
  // @ts-ignore
  const {setQueryFilters} = useSearchFilters();
  const [searchValue, setSearchValue] = useState(
    query[SEARCH_FILTERS.SEARCH] || ''
  );
  const debouncedSearchValue = useDebounce(searchValue, 700);
  const {
    push,
    query: {slug: searchType},
  } = useRouter();
  const {
    SEARCH_COURSES,
    SEARCH_USERS,
    SEARCH_UNITED_ROUTE,
    SEARCH_CLINICS,
    SEARCH_VACANCIES,
    SEARCH_POSTS,
  } = useRoutePaths();
  const {isLoading, setLoading} = useLoading();
  const isAuthorized = getAuthorizationToken();
  const {t} = useTranslation('common');

  useEffect(() => {
    if (
      isRouterReady &&
      query[SEARCH_FILTERS.SEARCH] &&
      !initialQuerySetRef.current
    ) {
      setSearchValue(query[SEARCH_FILTERS.SEARCH]);
      initialQuerySetRef.current = true;
    }
  }, [isRouterReady, query]);

  useEffect(() => {
    if (debouncedSearchValue !== searchValue) {
      setQueryFilters({
        [SEARCH_FILTERS.SEARCH]: searchValue,
      });
    }
  }, [debouncedSearchValue, searchValue]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const {responseBody, status} = await fetchCoursesForUnitedTab({
        searchQuery: debouncedSearchValue,
        locale: locale,
      });

      const {responseBody: topicsResponseBody, status: topicsStatus} =
        await fetchTopicsForUnitedTab({
          searchQuery: debouncedSearchValue,
          locale: locale,
        });

      const {responseBody: usersResponseBody, status: usersStatus} =
        await fetchUsersForUnitedTab({
          searchQuery: debouncedSearchValue,
          locale: locale,
        });

      const {responseBody: clinicsResponseBody, status: clinicsStatus} =
        await fetchClinicsForUnitedTab({
          searchQuery: debouncedSearchValue,
          locale: locale,
        });

      const {responseBody: vacanciesResponseBody, status: vacanciesStatus} =
        await fetchVacanciesForUnitedTab({
          searchQuery: debouncedSearchValue,
          locale,
        });

      setLoading(false);

      if (
        status === RESPONSE_STATUSES.SUCCESS &&
        usersStatus === RESPONSE_STATUSES.SUCCESS &&
        clinicsStatus === RESPONSE_STATUSES.SUCCESS &&
        vacanciesStatus === RESPONSE_STATUSES.SUCCESS &&
        topicsStatus === RESPONSE_STATUSES.SUCCESS
      ) {
        if (usersResponseBody.data.length) {
          setUsersData(
            usersResponseBody.data.map((user) => transformUserData(user))
          );
        } else {
          setUsersData([]);
        }

        if (responseBody.data.length) {
          setCoursesData(transforCoursesData(responseBody?.data, locale));
        } else {
          setCoursesData([]);
        }

        if (topicsResponseBody.data.length) {
          setTopicsData(getTransformedPosts(topicsResponseBody.data));
        } else {
          setTopicsData([]);
        }

        if (clinicsResponseBody.data.length) {
          setClinicsData(
            clinicsResponseBody.data.map((user) =>
              transformClinicDataForCard(user, locale)
            )
          );
        } else {
          setClinicsData([]);
        }

        if (vacanciesResponseBody?.data?.length) {
          setVacanciesData(
            vacanciesResponseBody.data.map((item) => {
              return getDataForVacancyItem(
                item,
                item.clinic,
                employmentTypes,
                locale,
                t
              );
            })
          );
        } else {
          setVacanciesData([]);
        }

        setFetchedFilters({query: debouncedSearchValue});
      }
    };

    if (
      (fetchedFilters.query === null ||
        fetchedFilters.query !== debouncedSearchValue) &&
      employmentTypes?.length
    ) {
      fetchData();
    }
  }, [debouncedSearchValue, locale, employmentTypes]);

  const handleSearchValueChange = useCallback((event) => {
    setSearchValue(event.target.value);
  }, []);

  const handleShowCoursesClick = useCallback(() => {
    push({
      pathname: `${SEARCH_COURSES.getLink()}`,
      query: {
        [SEARCH_FILTERS.OFFSET]: '0',
        [SEARCH_FILTERS.ORDER]: ORDER_FILTER.DESC,
        [SEARCH_FILTERS.LANGUAGE]: locale,
        [SEARCH_FILTERS.SEARCH]: searchValue,
      },
    });
  }, [searchValue]);

  const handleShowPostsClick = useCallback(() => {
    push({
      pathname: `${SEARCH_POSTS.getLink()}`,
      query: {
        [SEARCH_FILTERS.OFFSET]: '0',
        [SEARCH_FILTERS.ORDER]: ORDER_FILTER.DESC,
        [SEARCH_FILTERS.LANGUAGE]: locale,
        [SEARCH_FILTERS.SEARCH]: searchValue,
      },
    });
  }, [searchValue]);

  const handleShowUsersClick = useCallback(() => {
    push({
      pathname: `${SEARCH_USERS.getLink()}`,
      query: {
        [SEARCH_FILTERS.OFFSET]: '0',
        [SEARCH_FILTERS.ORDER]: ORDER_FILTER.DESC,
        [SEARCH_FILTERS.LANGUAGE]: locale,
        [SEARCH_FILTERS.SEARCH]: searchValue,
      },
    });
  }, [searchValue]);

  const handleShowVacanciesClick = useCallback(() => {
    push({
      pathname: SEARCH_VACANCIES.getLink(),
      query: {
        [SEARCH_FILTERS.OFFSET]: '0',
        [SEARCH_FILTERS.ORDER]: ORDER_FILTER.DESC,
        [SEARCH_FILTERS.LANGUAGE]: locale,
        [SEARCH_FILTERS.SEARCH]: searchValue,
      },
    });
  }, [locale, searchValue]);

  const handleSuccessfulApplicationCallback = useCallback(async () => {
    if (isAuthorized) {
      const {responseBody, status} = await fetchVacanciesForUnitedTab({
        searchQuery: debouncedSearchValue,
        locale,
      });

      if (status === RESPONSE_STATUSES.SUCCESS) {
        setVacanciesData(
          responseBody.data.map((item) => {
            return getDataForVacancyItem(
              item,
              item.clinic,
              employmentTypes,
              locale,
              t
            );
          })
        );
      }
    }
  }, [isAuthorized, debouncedSearchValue, locale]);

  const handleShowClinicsClick = useCallback(() => {
    push({
      pathname: `${SEARCH_CLINICS.getLink()}`,
      query: {
        [SEARCH_FILTERS.OFFSET]: '0',
        [SEARCH_FILTERS.ORDER]: ORDER_FILTER.DESC,
        [SEARCH_FILTERS.LANGUAGE]: locale,
        [SEARCH_FILTERS.SEARCH]: searchValue,
      },
    });
  }, [searchValue]);

  const handleTabClick = useCallback(
    (newSearchType) => {
      let newPathname = null;

      switch (newSearchType) {
        case SEARCH_TYPE.USERS: {
          newPathname = SEARCH_USERS.getLink();
          break;
        }
        case SEARCH_TYPE.COURSES: {
          newPathname = SEARCH_COURSES.getLink();
          break;
        }
        case SEARCH_TYPE.CLINICS: {
          newPathname = SEARCH_CLINICS.getLink();
          break;
        }
        case SEARCH_TYPE.UNITED: {
          newPathname = SEARCH_UNITED_ROUTE.getLink();
          break;
        }
        case SEARCH_TYPE.POSTS: {
          newPathname = SEARCH_POSTS.getLink();
          break;
        }
        case SEARCH_TYPE.VACANCIES: {
          newPathname = SEARCH_VACANCIES.getLink();
        }
      }

      push({
        pathname: newPathname,
        query: {
          [SEARCH_FILTERS.OFFSET]: '0',
          [SEARCH_FILTERS.ORDER]: ORDER_FILTER.DESC,
          [SEARCH_FILTERS.LANGUAGE]: locale,
          [SEARCH_FILTERS.SEARCH]: searchValue,
        },
      });
    },
    [searchValue]
  );

  return (
    <MultipleSearchLayout
      postsData={topicsData}
      vacanciesData={vacanciesData}
      onShowPostsClick={handleShowPostsClick}
      // @ts-ignore
      searchValue={searchValue}
      onSearchValueChange={handleSearchValueChange}
      coursesData={coursesData}
      usersData={usersData}
      clinicsData={clinicsData}
      onShowCoursesClick={handleShowCoursesClick}
      onShowUsersClick={handleShowUsersClick}
      onShowClinicClick={handleShowClinicsClick}
      onShowVacanciesClick={handleShowVacanciesClick}
      onSuccessfulApplicationCallback={handleSuccessfulApplicationCallback}
      // @ts-ignore
      activeSearchType={searchType}
      onTabClick={handleTabClick}
      isLoading={isLoading}
      currentUserId={user?.user?.id || null}
    />
  );
};

export default MultipleSearch;
